(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }
    callback();
  };

  /* Reveal-on-scroll */
  const setupReveal = () => {
    const items = [...document.querySelectorAll('[data-stars-reveal]')];
    if (!items.length) return;

    items.forEach((item) => item.classList.add('stars-animate-ready'));

    if (reducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('stars-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('stars-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.04 }
    );

    items.forEach((item) => observer.observe(item));
  };

  /* All Categories mega menu — click toggle, outside click, Escape */
  const closeMegaMenus = (except) => {
    document.querySelectorAll('.stars-mega.is-open').forEach((menu) => {
      if (menu === except) return;
      menu.classList.remove('is-open');
      const toggle = menu.querySelector('.stars-mega__toggle');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  };

  const setupMegaMenus = () => {
    document.querySelectorAll('.stars-mega').forEach((menu) => {
      const toggle = menu.querySelector('.stars-mega__toggle');
      if (!toggle) return;

      toggle.setAttribute('aria-expanded', 'false');
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        const nextOpen = !menu.classList.contains('is-open');
        closeMegaMenus(menu);
        menu.classList.toggle('is-open', nextOpen);
        toggle.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
      });
    });

    document.addEventListener('click', (event) => {
      if (event.target.closest('.stars-mega')) return;
      closeMegaMenus();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeMegaMenus();
      document.querySelectorAll('.stars-mobile-menu[open]').forEach((menu) => menu.removeAttribute('open'));
    });
  };

  /* Mobile drawer — close on outside click / link tap */
  const setupMobileMenu = () => {
    document.querySelectorAll('[data-stars-mobile-menu-close]').forEach((button) => {
      button.addEventListener('click', () => {
        const menu = button.closest('.stars-mobile-menu');
        if (!menu) return;
        menu.removeAttribute('open');
        const summary = menu.querySelector(':scope > summary');
        if (summary) summary.focus();
      });
    });

    document.addEventListener('click', (event) => {
      document.querySelectorAll('.stars-mobile-menu[open]').forEach((menu) => {
        if (menu.contains(event.target)) return;
        menu.removeAttribute('open');
      });
    });

    document.querySelectorAll('.stars-mobile-menu__panel a').forEach((link) => {
      link.addEventListener('click', () => {
        const menu = link.closest('.stars-mobile-menu');
        if (menu) menu.removeAttribute('open');
      });
    });
  };

  /* Offer bar rotator — fades between messages, pauses on hover/focus */
  const setupOfferBar = () => {
    const bar = document.querySelector('[data-stars-offers]');
    if (!bar) return;
    const track = bar.querySelector('.stars-offer-bar__track');
    if (track) track.classList.add('js-armed');
    const messages = [...bar.querySelectorAll('.stars-offer-bar__msg')];
    if (!messages.length) return;

    let index = messages.findIndex((m) => m.classList.contains('is-active'));
    if (index < 0) {
      index = 0;
      messages[0].classList.add('is-active');
    }
    if (messages.length < 2) return;

    if (reducedMotion()) return;

    let timer = null;
    const advance = () => {
      messages[index].classList.remove('is-active');
      index = (index + 1) % messages.length;
      messages[index].classList.add('is-active');
    };
    const start = () => {
      if (timer) return;
      timer = window.setInterval(advance, 6000);
    };
    const stop = () => {
      window.clearInterval(timer);
      timer = null;
    };

    bar.addEventListener('mouseenter', stop);
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', stop);
    bar.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    start();
  };

  /* Free shipping progress — live update after cart changes */
  const formatMoney = (cents) => {
    const value = cents / 100;
    return '$' + (Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2));
  };

  const renderShippingProgress = (totalCents, hasDiscount) => {
    document.querySelectorAll('[data-stars-shipping-progress]').forEach((el) => {
      const threshold = parseInt(el.dataset.threshold, 10) || 10000;
      const fill = el.querySelector('.stars-shipbar__fill');
      const label = el.querySelector('.stars-shipbar__label');
      const pct = Math.max(0, Math.min(100, Math.round((totalCents / threshold) * 100)));
      if (fill) fill.style.width = pct + '%';
      el.classList.toggle('is-qualified', !hasDiscount && totalCents >= threshold);
      if (label) {
        if (hasDiscount) {
          label.textContent = 'A discount is applied. Standard Shipping remains at its normal checkout price because offers do not stack.';
        } else if (totalCents >= threshold) {
          label.textContent = 'Eligible for free Standard Shipping (2–8 business days) at checkout. Faster shipping remains regular price.';
        } else {
          label.textContent = "You're " + formatMoney(threshold - totalCents) + ' away from eligibility for free Standard Shipping (2–8 business days). One offer per order.';
        }
      }
    });
  };

  const setupShippingProgress = () => {
    if (!document.querySelector('[data-stars-shipping-progress]')) return;

    const refresh = () => {
      fetch(window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js')
        .then((response) => response.json())
        .then((cart) => {
          const cartDiscounts = [
            ...(cart.cart_level_discount_applications || []),
            ...(cart.discount_applications || []),
          ];
          const lineDiscounts = (cart.items || []).some((item) =>
            (item.line_level_discount_allocations || []).length > 0
          );
          renderShippingProgress(cart.total_price, cartDiscounts.length > 0 || lineDiscounts);
        })
        .catch(() => {});
    };

    if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS === 'object' && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, refresh);
    }
    document.addEventListener('change', (event) => {
      if (event.target.closest('cart-items, cart-drawer-items')) window.setTimeout(refresh, 800);
    });
  };

  /* Selected variant summary + quote prefill — Liquid re-render covers most updates;
     this keeps them in sync instantly on option change via Dawn's pubsub */
  const setupVariantSummary = () => {
    if (typeof window.subscribe !== 'function' || typeof window.PUB_SUB_EVENTS !== 'object') return;
    const eventName = window.PUB_SUB_EVENTS.variantChange;
    if (!eventName) return;
    window.subscribe(eventName, (event) => {
      const variant = event && event.data && event.data.variant;
      if (!variant) return;
      const summary = document.querySelector('[data-stars-selected-summary]');
      if (summary) {
        summary.classList.remove('is-updated');
        (variant.options || []).forEach((value, index) => {
          const el = summary.querySelector('[data-stars-sum-opt="' + index + '"]');
          if (el) el.textContent = value;
        });
        const skuEl = summary.querySelector('[data-stars-sum-sku]');
        if (skuEl && variant.sku) skuEl.textContent = variant.sku;
        window.requestAnimationFrame(() => summary.classList.add('is-updated'));
      }
      const quoteVariant = document.querySelector('[data-stars-quote-variant]');
      if (quoteVariant && variant.title) quoteVariant.value = variant.title;
    });
  };

  /* Uniform Package Builder — live recommended-package summary */
  const setupPackageBuilder = () => {
    const form = document.getElementById('StarsPackageBuilder');
    if (!form) return;
    const title = form.querySelector('[data-builder-summary-title]');
    const body = form.querySelector('[data-builder-summary-body]');
    if (!title || !body) return;

    const refresh = () => {
      const audience = form.querySelector('input[name="contact[team_type]"]:checked');
      const size = form.querySelector('input[name="contact[team_size]"]:checked');
      const custom = form.querySelector('input[name="contact[customization]"]:checked');
      const needs = [...form.querySelectorAll('input[name="contact[items_needed]"]:checked')].map((i) => i.value);

      title.textContent = (audience ? audience.value : 'Team') + ' package';
      const parts = [];
      if (size) parts.push('Team size: ' + size.value);
      parts.push(needs.length ? 'Items: ' + needs.join(', ') : 'Items: pick what you need above');
      if (custom) parts.push('Customization: ' + custom.value);
      body.textContent = parts.join('\n');

      /* v2 summary rows + progress */
      const set = (sel, val) => { const el = form.querySelector(sel); if (el) el.textContent = val; };
      set('[data-pb-type]', audience ? audience.value : '—');
      set('[data-pb-size]', size ? size.value : '—');
      set('[data-pb-items]', needs.length ? needs.join(', ') : 'Select items above');
      set('[data-pb-custom]', custom ? custom.value : '—');
      const fill = form.querySelector('[data-pb-progress]');
      if (fill) {
        let done = 2; /* team type + customization have defaults */
        if (size) done += 1;
        if (needs.length) done += 1;
        fill.style.width = (done / 4 * 100) + '%';
      }
      const summaryCard = form.querySelector('[data-builder-summary]');
      if (summaryCard) {
        summaryCard.classList.remove('is-updated');
        window.requestAnimationFrame(() => summaryCard.classList.add('is-updated'));
      }
    };

    form.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"], input[type="checkbox"]')) refresh();
    });
    refresh();
  };

  /* Recently viewed — standard localStorage only, capped at 8, hidden when empty */
  const setupRecentlyViewed = () => {
    const sections = document.querySelectorAll('[data-stars-recent]');
    if (!sections.length) return;
    const KEY = 'stars-recently-viewed';
    let items = [];
    try { items = JSON.parse(window.localStorage.getItem(KEY) || '[]'); } catch (e) { items = []; }

    let currentHandle = null;
    sections.forEach((section) => {
      const raw = section.getAttribute('data-stars-recent-current');
      if (!raw) return;
      try {
        const current = JSON.parse(raw);
        currentHandle = current.handle;
        items = items.filter((i) => i.handle !== current.handle);
        items.unshift(current);
        items = items.slice(0, 8);
        window.localStorage.setItem(KEY, JSON.stringify(items));
      } catch (e) { /* ignore malformed */ }
    });

    sections.forEach((section) => {
      const grid = section.querySelector('[data-stars-recent-grid]');
      if (!grid) return;
      const show = items.filter((i) => i.handle !== currentHandle).slice(0, 6);
      if (!show.length) return;
      grid.innerHTML = show.map((i) =>
        '<a class="stars-kit__card" href="' + i.url + '">' +
        '<span class="stars-kit__media"><img src="' + i.image + '" alt="" loading="lazy"></span>' +
        '<span class="stars-kit__title"></span>' +
        '<span class="stars-kit__price"></span>' +
        '</a>'
      ).join('');
      grid.querySelectorAll('.stars-kit__title').forEach((el, idx) => { el.textContent = show[idx].title; });
      grid.querySelectorAll('.stars-kit__price').forEach((el, idx) => { el.textContent = show[idx].price; });
      section.hidden = false;
    });
  };

  /* Modal shells — open by trigger/delay/exit-intent; close via button, backdrop, Escape.
     Each modal shows at most once per session. All OFF unless enabled in the theme editor. */
  const setupModals = () => {
    const modals = document.querySelectorAll('[data-stars-modal]');
    if (!modals.length) return;

    const openModal = (modal) => {
      const name = modal.getAttribute('data-stars-modal');
      const key = 'stars-modal-' + name;
      try {
        if (window.sessionStorage.getItem(key)) return;
        /* permanent opt-out ("Don't show this again") blocks auto-opens only */
        if (name === 'newsletter' && window.localStorage.getItem('stars:nl-optout')) return;
      } catch (e) { /* continue */ }
      modal.setAttribute('open', '');
      document.documentElement.classList.add('stars-modal-lock');
      try { window.sessionStorage.setItem(key, '1'); } catch (e) { /* ignore */ }
      const focusable = modal.querySelector('button, a, input');
      if (focusable) focusable.focus();
    };

    /* "Don't show this again" checkbox — persists across visits */
    document.querySelectorAll('[data-nl-optout]').forEach((box) => {
      try { box.checked = !!window.localStorage.getItem('stars:nl-optout'); } catch (e) { /* ignore */ }
      box.addEventListener('change', () => {
        try {
          if (box.checked) { window.localStorage.setItem('stars:nl-optout', '1'); }
          else { window.localStorage.removeItem('stars:nl-optout'); }
        } catch (e) { /* ignore */ }
      });
    });
    const closeModal = (modal) => {
      if (!modal.hasAttribute('open') || modal.classList.contains('is-closing')) return;
      if (reducedMotion()) {
        modal.removeAttribute('open');
        if (!document.querySelector('.stars-modal[open]')) {
          document.documentElement.classList.remove('stars-modal-lock');
        }
        return;
      }
      modal.classList.add('is-closing');
      const panel = modal.querySelector('.stars-modal__panel');
      let done = false;
      const finish = () => {
        if (done) return; done = true;
        modal.removeAttribute('open');
        modal.classList.remove('is-closing');
        if (!document.querySelector('.stars-modal[open]')) {
          document.documentElement.classList.remove('stars-modal-lock');
        }
      };
      if (panel) panel.addEventListener('animationend', finish, { once: true });
      window.setTimeout(finish, 380);
    };

    modals.forEach((modal) => {
      modal.querySelectorAll('[data-stars-modal-close]').forEach((el) =>
        el.addEventListener('click', () => closeModal(modal))
      );
      const delay = parseInt(modal.getAttribute('data-stars-modal-delay') || '', 10);
      if (delay) window.setTimeout(() => openModal(modal), delay * 1000);
      if (modal.hasAttribute('data-stars-modal-exit') && window.matchMedia('(hover: hover)').matches) {
        document.addEventListener('mouseout', (event) => {
          if (!event.relatedTarget && event.clientY <= 0) openModal(modal);
        });
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { modals.forEach(closeModal); return; }
      if (event.key !== 'Tab') return;
      const open = document.querySelector('.stars-modal[open]');
      if (!open) return;
      const panel = open.querySelector('.stars-modal__panel');
      if (!panel) return;
      const nodes = panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    });
    document.querySelectorAll('[data-stars-modal-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const target = document.querySelector('[data-stars-modal="' + trigger.getAttribute('data-stars-modal-open') + '"]');
        if (!target) return;
        target.classList.remove('is-closing');
        target.setAttribute('open', '');
        document.documentElement.classList.add('stars-modal-lock');
        const focusable = target.querySelector('.stars-modal__x') || target.querySelector('.stars-modal__body a, .stars-modal__body button, .stars-modal__body input');
        if (focusable) focusable.focus();
      });
    });
  };

  /* Product-card color swatches — preview a real variant image on hover/focus/tap.
     Truthful: swatches + images come straight from the product's variants. */
  const setupCardSwatches = () => {
    const groups = document.querySelectorAll('[data-stars-swatches]');
    if (!groups.length) return;
    groups.forEach((group) => {
      const root = group.closest('.stars-product-card, .card-wrapper');
      if (!root) return;
      const img = root.querySelector('.stars-product-card__media img, .card__media img');
      if (!img) return;
      const swatches = group.querySelectorAll('.stars-swatch[data-swatch-img]');
      if (!swatches.length) return;
      const origSrc = img.getAttribute('src');
      const origSrcset = img.getAttribute('srcset');
      const links = Array.from(root.querySelectorAll('a[href*="/products/"]'));
      const origHrefs = links.map((a) => a.getAttribute('href'));
      const preview = (sw) => {
        group.querySelectorAll('.stars-swatch').forEach((s) => s.classList.remove('is-active'));
        sw.classList.add('is-active');
        root.classList.add('sw-preview');
        img.setAttribute('src', sw.getAttribute('data-swatch-img'));
        img.removeAttribute('srcset');
        const url = sw.getAttribute('data-swatch-url');
        if (url) links.forEach((a) => a.setAttribute('href', url));
      };
      const restore = () => {
        group.querySelectorAll('.stars-swatch').forEach((s) => s.classList.remove('is-active'));
        root.classList.remove('sw-preview');
        if (origSrc) img.setAttribute('src', origSrc);
        if (origSrcset) { img.setAttribute('srcset', origSrcset); } else { img.removeAttribute('srcset'); }
        links.forEach((a, i) => { if (origHrefs[i] != null) a.setAttribute('href', origHrefs[i]); });
      };
      swatches.forEach((sw) => {
        sw.addEventListener('mouseenter', () => preview(sw));
        sw.addEventListener('focus', () => preview(sw));
        sw.addEventListener('click', (event) => { event.preventDefault(); preview(sw); });
      });
      root.addEventListener('mouseleave', restore);
    });
  };


  /* Wishlist shell — localStorage only, no external calls. Button stays hidden without JS. */
  const setupWishlist = () => {
    const btn = document.querySelector('[data-stars-wishlist]');
    if (!btn) return;
    const KEY = 'stars:wishlist';
    const readList = () => {
      try { return JSON.parse(window.localStorage.getItem(KEY) || '[]'); } catch (e) { return []; }
    };
    const handle = btn.getAttribute('data-handle');
    const paint = (saved) => {
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      btn.innerHTML = saved ? '&#9829; Saved for later' : '&#9825; Save for later';
    };
    btn.setAttribute('data-active', '');
    paint(readList().indexOf(handle) !== -1);
    btn.addEventListener('click', () => {
      let list = readList();
      const has = list.indexOf(handle) !== -1;
      list = has ? list.filter((h) => h !== handle) : list.concat(handle).slice(-40);
      try { window.localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* ignore */ }
      paint(!has);
    });
  };

  /* Print button (size guide) */
  const setupPrint = () => {
    document.querySelectorAll('[data-stars-print]').forEach((btn) =>
      btn.addEventListener('click', () => window.print())
    );
  };

  /* Floating help/support menu — open/close, Escape, outside-click, focus move.
     No AI, no network: it's a menu of real support links. */
  const setupHelpWidget = () => {
    const root = document.querySelector('[data-stars-help]');
    if (!root) return;
    const toggle = root.querySelector('[data-stars-help-toggle]');
    const panel = root.querySelector('[data-stars-help-panel]');
    if (!toggle || !panel) return;
    const open = () => {
      panel.hidden = false;
      root.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      const first = panel.querySelector('a, button');
      if (first) first.focus();
    };
    const close = (refocus) => {
      panel.hidden = true;
      root.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      if (refocus) toggle.focus();
    };
    toggle.addEventListener('click', () => (root.classList.contains('is-open') ? close(false) : open()));
    root.querySelectorAll('[data-stars-help-close]').forEach((el) =>
      el.addEventListener('click', () => close(true))
    );
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && root.classList.contains('is-open')) close(true);
    });
    document.addEventListener('click', (event) => {
      if (root.classList.contains('is-open') && !root.contains(event.target)) close(false);
    });
  };

  /* Product customization planner — carries the checked placements/types onto the
     quote link as query params so the request arrives pre-filled. No prices, no cart. */
  const setupProductCustomization = () => {
    const cta = document.querySelector('[data-stars-pcustom-cta]');
    if (!cta) return;
    const base = cta.getAttribute('href');
    const opts = document.querySelectorAll('[data-stars-pcustom-opt]');
    if (!opts.length) return;
    const update = () => {
      const chosen = Array.from(opts).filter((o) => o.checked).map((o) => o.value);
      cta.setAttribute('href', chosen.length ? base + '?customization=' + encodeURIComponent(chosen.join(', ')) : base);
    };
    opts.forEach((o) => o.addEventListener('change', update));
  };

  /* Variant guard — when the (Dawn) Add-to-Cart is disabled because the chosen
     combination is unavailable, show a clear helper message. Products with only a
     default variant never show it. */
  const setupVariantGuard = () => {
    const submit = document.querySelector('.product-form__submit');
    if (!submit) return;
    const picker = document.querySelector('variant-selects, variant-radios');
    if (!picker) return; // default-variant-only product: nothing to guard
    const msg = document.createElement('p');
    msg.className = 'stars-variant-msg';
    msg.setAttribute('role', 'status');
    msg.textContent = 'This combination is unavailable — please select a different color or size before adding to cart.';
    submit.insertAdjacentElement('afterend', msg);
    const sync = () => {
      const blocked = submit.hasAttribute('disabled') || submit.getAttribute('aria-disabled') === 'true';
      msg.classList.toggle('is-active', blocked);
      const stickyBtn = document.querySelector('[data-stars-sticky-add]');
      if (stickyBtn) stickyBtn.toggleAttribute('disabled', blocked);
    };
    sync();
    new MutationObserver(sync).observe(submit, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
  };

  /* Sticky mobile Add-to-Cart bar — mobile only; appears when the real buy box
     scrolls out of view; mirrors price/variant/disabled state; proxies the real
     submit so all Dawn cart logic stays intact. */
  const setupStickyAtc = () => {
    const bar = document.querySelector('[data-stars-sticky-atc]');
    const submit = document.querySelector('.product-form__submit');
    if (!bar || !submit) return;
    bar.removeAttribute('hidden');
    bar.setAttribute('aria-hidden', 'false');
    const mq = window.matchMedia('(max-width: 749px)');
    const proxy = bar.querySelector('[data-stars-sticky-add]');
    if (proxy) {
      proxy.toggleAttribute('disabled', submit.hasAttribute('disabled') || submit.getAttribute('aria-disabled') === 'true');
      proxy.addEventListener('click', () => submit.click());
    }
    const show = (on) => {
      bar.classList.toggle('is-visible', on);
      document.body.classList.toggle('stars-sticky-atc-on', on);
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        const visible = entries[0].isIntersecting;
        show(mq.matches && !visible);
      }, { rootMargin: '-80px 0px 0px 0px' });
      io.observe(submit);
    } else {
      show(mq.matches);
    }
    if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS === 'object' && window.PUB_SUB_EVENTS.variantChange) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event && event.data && event.data.variant;
        if (!variant) return;
        const priceEl = bar.querySelector('[data-stars-sticky-price]');
        if (priceEl && typeof variant.price === 'number') {
          priceEl.textContent = '$' + (variant.price / 100).toFixed(2);
        }
        const sumEl = bar.querySelector('[data-stars-sticky-summary]');
        if (sumEl && variant.title) sumEl.textContent = variant.title;
      });
    }
  };

  /* Scroll-progress back-to-top — vanilla SVG ring, bottom-left (Help widget owns
     bottom-right). Appears after ~1.5 screens. */
  const setupBackToTop = () => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'stars-btt';
    btn.setAttribute('aria-label', 'Back to top');
    const R = 21;
    const C = (2 * Math.PI * R).toFixed(1);
    btn.innerHTML = '<svg viewBox="0 0 46 46" aria-hidden="true">'
      + '<circle class="stars-btt__track" cx="23" cy="23" r="' + R + '"/>'
      + '<circle class="stars-btt__bar" cx="23" cy="23" r="' + R + '" stroke-dasharray="' + C + '" stroke-dashoffset="' + C + '"/>'
      + '</svg><span class="stars-btt__arrow" aria-hidden="true">&uarr;</span>';
    document.body.appendChild(btn);
    const bar = btn.querySelector('.stars-btt__bar');
    const circumference = parseFloat(C);
    let ticking = false;
    const update = () => {
      ticking = false;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      bar.style.strokeDashoffset = (circumference * (1 - progress)).toFixed(1);
      btn.classList.toggle('is-visible', window.scrollY > window.innerHeight * 1.2);
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    update();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' }));
  };

  /* Slide-out cart drawer — self-contained, progressive-enhancement.
     Opens from the cart icon; add-to-cart adds via AJAX and opens the drawer, with a
     hard fallback to a normal submit / /cart navigation if anything fails. */
  const setupCartDrawer = () => {
    const drawer = document.querySelector('[data-stars-cart-drawer]');
    if (!drawer) return;
    const body = drawer.querySelector('[data-cart-body]');
    const footer = drawer.querySelector('[data-cart-footer]');
    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    const countLabel = drawer.querySelector('[data-cart-count-label]');
    if (!body) return;
    let lastFocus = null;
    const esc = (str) => String(str == null ? '' : str).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const money = (cents) => '$' + (cents / 100).toFixed(2);

    const updateCounts = (count) => {
      document.querySelectorAll('.stars-cart-link span[aria-hidden], .stars-bottom-nav__count').forEach((el) => { el.textContent = count; });
      document.querySelectorAll('.stars-cart-link').forEach((el) => {
        el.setAttribute('aria-label', 'Cart with ' + count + (count === 1 ? ' item' : ' items'));
      });
      if (countLabel) countLabel.textContent = count ? '(' + count + ')' : '';
    };

    const render = (cart) => {
      updateCounts(cart.item_count);
      if (!cart.item_count) {
        body.innerHTML = '<div class="stars-cd__empty"><p>Your cart is empty.</p><a class="stars-btn stars-btn--outline" href="/collections/all">Shop Uniforms</a></div>';
        if (footer) footer.hidden = true;
        return;
      }
      const rows = cart.items.map((item, i) => {
        const line = i + 1;
        const img = item.image ? '<img src="' + item.image.replace(/(_(\d+)x(\d+))?\.(jpg|png|jpeg|webp)/, '_120x.$4') + '" alt="' + esc(item.product_title) + '" width="60" height="60" loading="lazy">' : '';
        const opts = (item.variant_title && item.variant_title !== 'Default Title') ? '<span class="stars-cd__opts">' + esc(item.variant_title) + '</span>' : '';
        return '<div class="stars-cd__item">'
          + '<a class="stars-cd__thumb" href="' + esc(item.url) + '">' + img + '</a>'
          + '<div class="stars-cd__info">'
          + '<a class="stars-cd__title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>'
          + opts
          + '<div class="stars-cd__row">'
          + '<div class="stars-cd__qty">'
          + '<button type="button" data-cart-qty="' + (item.quantity - 1) + '" data-line="' + line + '" aria-label="Decrease quantity">&minus;</button>'
          + '<span>' + item.quantity + '</span>'
          + '<button type="button" data-cart-qty="' + (item.quantity + 1) + '" data-line="' + line + '" aria-label="Increase quantity">+</button>'
          + '</div>'
          + '<span class="stars-cd__price">' + money(item.final_line_price) + '</span>'
          + '</div>'
          + '<button type="button" class="stars-cd__remove" data-cart-remove data-line="' + line + '">Remove</button>'
          + '</div></div>';
      }).join('');
      body.innerHTML = rows;
      if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
      if (footer) footer.hidden = false;
    };

    const refresh = () => fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json()).then(render).catch(() => {});

    const open = () => {
      drawer.removeAttribute('hidden');
      lastFocus = document.activeElement;
      window.requestAnimationFrame(() => drawer.classList.add('is-open'));
      document.documentElement.style.overflow = 'hidden';
      refresh();
      const closeBtn = drawer.querySelector('.stars-cart-drawer__x');
      if (closeBtn) closeBtn.focus();
    };
    const close = () => {
      drawer.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      window.setTimeout(() => drawer.setAttribute('hidden', ''), 320);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    };

    document.querySelectorAll('.stars-cart-link, .stars-bottom-nav__item[href*="/cart"]').forEach((el) => {
      el.addEventListener('click', (event) => { event.preventDefault(); open(); });
    });
    drawer.querySelectorAll('[data-cart-close]').forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    /* keep header/bottom-nav counts live when Dawn's cart page updates quantities */
    const refreshCounts = () => {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => updateCounts(cart.item_count))
        .catch(() => {});
    };
    if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS === 'object' && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, refreshCounts);
    }
    refreshCounts();

    const changeLine = (line, qty) => fetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: parseInt(line, 10), quantity: qty }),
    }).then((r) => r.json()).then(render).catch(() => {});
    body.addEventListener('click', (event) => {
      const rm = event.target.closest('[data-cart-remove]');
      const qb = event.target.closest('[data-cart-qty]');
      if (rm) { event.preventDefault(); changeLine(rm.getAttribute('data-line'), 0); }
      else if (qb) { event.preventDefault(); changeLine(qb.getAttribute('data-line'), parseInt(qb.getAttribute('data-cart-qty'), 10)); }
    });

    /* Intercept add-to-cart (capture phase) → AJAX add + open drawer; fall back safely. */
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      const action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const data = new FormData(form);
      const oldError = form.querySelector('.stars-atc-error');
      if (oldError) oldError.remove();
      fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data })
        .then((r) => r.json().then((payload) => ({ ok: r.ok, payload })).catch(() => ({ ok: r.ok, payload: null })))
        .then(({ ok, payload }) => {
          if (ok) { open(); return; }
          /* Shopify rejected the add (e.g. stock limit) — show the reason inline */
          const msg = (payload && (payload.description || payload.message)) || 'Unable to add this item to your cart.';
          const note = document.createElement('p');
          note.className = 'stars-atc-error';
          note.setAttribute('role', 'alert');
          note.textContent = msg;
          const btn = form.querySelector('[type="submit"]');
          if (btn && btn.parentNode) btn.parentNode.insertBefore(note, btn.nextSibling);
          else form.appendChild(note);
        })
        .catch(() => { try { form.submit(); } catch (e) { window.location = '/cart'; } });
    }, true);
  };

  /* Honest helper toast — bottom-left, shows one dismissible prompt after a delay,
     once per session. No fake purchase/activity notifications. */
  const setupToasts = () => {
    const wrap = document.querySelector('[data-stars-toasts]');
    if (!wrap) return;
    const toast = wrap.querySelector('[data-stars-toast]');
    const dataEl = document.querySelector('[data-stars-toast-products]');
    if (!toast || !dataEl) return;
    try { if (window.sessionStorage.getItem('stars:toast-off')) return; } catch (e) { /* continue */ }
    let products = [];
    try { products = JSON.parse(dataEl.textContent) || []; } catch (e) { return; }
    if (!products.length) return;

    const CHIPS = ['Featured product', 'Uniform essential', 'Catalog highlight', 'Explore this item'];
    const SHOW_MS = 8000;
    const GAP_MS = 16000;
    let idx = 0;
    try { idx = parseInt(window.localStorage.getItem('stars:toast-idx') || '0', 10) % products.length; } catch (e) { /* ignore */ }
    let hideTimer = null;
    let nextTimer = null;
    let stopped = false;

    const imgEl = toast.querySelector('[data-toast-img]');
    const chipEl = toast.querySelector('[data-toast-chip]');
    const titleEl = toast.querySelector('[data-toast-title]');
    const priceEl = toast.querySelector('[data-toast-price]');

    const hide = (fast) => {
      window.clearTimeout(hideTimer);
      if (!toast.classList.contains('is-showing')) return;
      toast.classList.remove('is-showing');
      if (fast || reducedMotion()) { toast.hidden = true; return; }
      toast.classList.add('is-leaving');
      window.setTimeout(() => { toast.classList.remove('is-leaving'); toast.hidden = true; }, 340);
    };

    const show = () => {
      if (stopped || document.hidden) { schedule(); return; }
      /* never compete with an open popup */
      if (document.querySelector('.stars-modal[open], .stars-thanks, .stars-toast--a250:not([hidden])')) { schedule(); return; }
      const item = products[idx % products.length];
      const chip = CHIPS[idx % CHIPS.length];
      idx += 1;
      try { window.localStorage.setItem('stars:toast-idx', String(idx % products.length)); } catch (e) { /* ignore */ }
      if (imgEl) { imgEl.src = item.img; imgEl.alt = item.title; }
      if (chipEl) chipEl.textContent = chip;
      if (titleEl) titleEl.textContent = item.title;
      if (priceEl) priceEl.textContent = item.price;
      toast.setAttribute('href', item.url);
      toast.hidden = false;
      toast.classList.remove('is-leaving');
      window.requestAnimationFrame(() => toast.classList.add('is-showing'));
      hideTimer = window.setTimeout(() => { hide(); schedule(); }, SHOW_MS);
    };

    const schedule = () => {
      window.clearTimeout(nextTimer);
      if (stopped) return;
      nextTimer = window.setTimeout(show, GAP_MS);
    };

    const delay = (parseInt(wrap.getAttribute('data-toast-delay'), 10) || 9) * 1000;
    window.setTimeout(show, delay);

    const close = toast.querySelector('[data-stars-toast-close]');
    if (close) {
      close.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        stopped = true;
        window.clearTimeout(nextTimer);
        hide();
        try { window.sessionStorage.setItem('stars:toast-off', '1'); } catch (e) { /* ignore */ }
      });
    }
    document.addEventListener('visibilitychange', () => { if (document.hidden) hide(true); });
  };

  /* Designer pages — live summary sync: inputs with data-ds-watch update
     matching [data-ds-out] slots in the sticky summary card. */
  const setupDesigners = () => {
    const watched = document.querySelectorAll('[data-ds-watch]');
    if (!watched.length) return;
    const update = (key, value) => {
      document.querySelectorAll('[data-ds-out="' + key + '"]').forEach((out) => {
        const prefix = out.getAttribute('data-ds-prefix') || '';
        out.textContent = value ? prefix + value : (prefix ? '' : '\u2014');
        const card = out.closest('.stars-builder__summarycard');
        if (card) {
          card.classList.remove('is-updated');
          window.requestAnimationFrame(() => card.classList.add('is-updated'));
        }
      });
    };
    watched.forEach((input) => {
      const key = input.getAttribute('data-ds-watch');
      const apply = () => {
        if (input.type === 'radio') { if (input.checked) update(key, input.value); }
        else update(key, input.value.trim());
      };
      input.addEventListener('change', apply);
      if (input.type === 'text') input.addEventListener('input', apply);
      if (input.type === 'radio' && input.checked) apply();
    });

    /* badge design grid: filter cards by finish */
    const filters = document.querySelectorAll('[data-bd-filter]');
    if (filters.length) {
      const cards = document.querySelectorAll('.stars-designer__style[data-finish]');
      filters.forEach((btn) => {
        btn.addEventListener('click', () => {
          const key = btn.getAttribute('data-bd-filter');
          filters.forEach((b) => b.classList.toggle('is-active', b === btn));
          cards.forEach((card) => {
            card.hidden = key !== 'all' && card.getAttribute('data-finish') !== key;
          });
        });
      });
    }
  };

  /* Condensing sticky header — tucks the announcement + contact strips and
     shrinks the logo after the user scrolls down; restores near the top. */
  const setupCondensingHeader = () => {
    const header = document.querySelector('.stars-header');
    if (!header) return;
    let ticking = false;
    const apply = () => {
      ticking = false;
      header.classList.toggle('stars-header--condensed', window.scrollY > 150);
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; window.requestAnimationFrame(apply); }
    }, { passive: true });
    apply();
  };

  /* America 250 campaign runtime — FIXED absolute end date from theme settings
     (identical for every visitor; never per-session, never reset on refresh).
     Drives the countdown, the graceful expired state, the once-per-session
     auto popup (only when deal rotation is off), and the honest campaign toast. */
  const setupA250 = () => {
    const cfgEl = document.querySelector('[data-a250-config]');
    if (!cfgEl) return;
    let cfg = null;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    if (!cfg || !cfg.enabled) return;

    const endMs = cfg.end ? Date.parse(cfg.end) : NaN;
    /* require an explicit UTC offset (or Z) so the end is one absolute instant
       everywhere — a naive local timestamp must not silently vary by visitor */
    const hasEnd = !isNaN(endMs) && /(Z|[+-]\d{2}:?\d{2})\s*$/.test(String(cfg.end || ''));
    const isExpired = () => hasEnd && Date.now() > endMs;

    const counts = [...document.querySelectorAll('[data-a250-countdown]')];
    const markExpired = () => {
      document.documentElement.classList.add('stars-a250-expired');
      counts.forEach((el) => {
        const blocks = el.querySelector('.stars-a250-count__blocks');
        const label = el.querySelector('[data-a250-count-label]');
        const done = el.querySelector('[data-a250-count-done]');
        if (blocks) blocks.hidden = true;
        if (label) label.hidden = true;
        if (done) done.hidden = false;
        el.hidden = false;
        el.removeAttribute('role');
        el.removeAttribute('aria-live');
      });
    };

    if (isExpired()) {
      markExpired();
    } else if (hasEnd && counts.length) {
      const pad = (n) => (n < 10 ? '0' : '') + n;
      counts.forEach((el) => { el.hidden = false; });
      let lastAria = 0;
      let timer = null;
      const tick = () => {
        const diff = endMs - Date.now();
        if (diff <= 0) { if (timer) window.clearInterval(timer); markExpired(); return; }
        const d = Math.floor(diff / 86400000);
        const h = Math.floor(diff % 86400000 / 3600000);
        const m = Math.floor(diff % 3600000 / 60000);
        const sec = Math.floor(diff % 60000 / 1000);
        counts.forEach((el) => {
          const set = (k, v) => {
            const t = el.querySelector('[data-a250-' + k + ']');
            if (t && t.textContent !== v) t.textContent = v;
          };
          set('dd', pad(d)); set('hh', pad(h)); set('mm', pad(m)); set('ss', pad(sec));
        });
        /* screen readers get a calm once-a-minute label, not a ticking feed */
        if (Date.now() - lastAria > 60000) {
          lastAria = Date.now();
          counts.forEach((el) => {
            const label = el.querySelector('[data-a250-count-label]');
            el.setAttribute('aria-label', ((label && label.textContent) || 'Campaign ends in') + ' ' + d + ' days, ' + h + ' hours, ' + m + ' minutes');
          });
        }
      };
      timer = window.setInterval(tick, 1000);
      tick();
    }

    const root = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');
    const rootTrim = root.replace(/\/+$/, '');
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    const isHome = path === (rootTrim || '/');
    const isLanding = window.location.pathname.indexOf(rootTrim + '/pages/america-250') === 0;

    /* auto popup — homepage, once per session, scroll-armed; only when the
       generic deal rotation isn't handling popups (it takes a250 first). */
    if (cfg.popup && !isExpired() && isHome && !document.querySelector('[data-stars-deal-rotation]')) {
      let popSeen = false;
      try { popSeen = !!window.sessionStorage.getItem('stars:a250-pop'); } catch (e) { /* ignore */ }
      const modal = document.querySelector('[data-stars-modal="a250"]');
      if (modal && !popSeen && !document.querySelector('.stars-form-success')) {
        let fired = false;
        const onScroll = () => {
          if (fired || window.scrollY < 180 || isExpired()) return;
          if (document.querySelector('.stars-modal[open], .stars-thanks')) return;
          fired = true;
          window.removeEventListener('scroll', onScroll);
          modal.classList.remove('is-closing');
          modal.setAttribute('open', '');
          document.documentElement.classList.add('stars-modal-lock');
          try {
            window.sessionStorage.setItem('stars:a250-pop', '1');
            window.sessionStorage.setItem('stars:deal-last', String(Date.now()));
          } catch (e) { /* ignore */ }
          const focusable = modal.querySelector('.stars-modal__x');
          if (focusable) focusable.focus();
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      }
    }

    /* honest campaign toast — once per session, dismissible, no fake activity */
    const toast = document.querySelector('[data-a250-toast]');
    if (toast && cfg.toast && !isExpired() && !isLanding) {
      let seen = false;
      try {
        seen = !!window.sessionStorage.getItem('stars:a250-toast') || !!window.sessionStorage.getItem('stars:toast-off');
      } catch (e) { /* ignore */ }
      if (!seen) {
        const msgs = [
          { t: 'Explore the Stars Uniform America 250 Collection', u: cfg.collectionUrl || '/pages/america-250' },
          { t: 'Need patriotic patches or badges? Request a custom quote', u: '/pages/custom-uniform-services#quote' },
          { t: 'Outfitting a team? Stars Uniform can help', u: '/pages/bulk-uniform-orders' },
          { t: 'Explore the Stars Uniform America 250 celebration', u: '/pages/america-250' }
        ];
        let mi = 0;
        try {
          mi = (parseInt(window.localStorage.getItem('stars:a250-toast-idx') || '0', 10) + 1) % msgs.length;
          window.localStorage.setItem('stars:a250-toast-idx', String(mi));
        } catch (e) { /* ignore */ }
        const msg = msgs[mi];
        const txt = toast.querySelector('[data-a250-toast-text]');
        if (txt) txt.textContent = msg.t;
        const link = toast.querySelector('[data-a250-toast-link]');
        if (link) link.setAttribute('href', msg.u);
        let tries = 0;
        const attempt = () => {
          if (isExpired() || tries > 20) return;
          tries += 1;
          if (document.hidden
              || document.querySelector('.stars-modal[open], .stars-thanks')
              || document.querySelector('.stars-toast--product:not([hidden])')) {
            window.setTimeout(attempt, 6000);
            return;
          }
          toast.hidden = false;
          window.requestAnimationFrame(() => toast.classList.add('is-showing'));
          try { window.sessionStorage.setItem('stars:a250-toast', '1'); } catch (e) { /* ignore */ }
          window.setTimeout(() => {
            toast.classList.remove('is-showing');
            if (reducedMotion()) { toast.hidden = true; return; }
            toast.classList.add('is-leaving');
            window.setTimeout(() => { toast.hidden = true; toast.classList.remove('is-leaving'); }, 340);
          }, 9000);
        };
        window.setTimeout(attempt, 19000);
        const x = toast.querySelector('[data-a250-toast-close]');
        if (x) {
          x.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            toast.classList.remove('is-showing');
            toast.hidden = true;
            try { window.sessionStorage.setItem('stars:a250-toast', '1'); } catch (e) { /* ignore */ }
          });
        }
      }
    }
  };

  /* Randomized deal-popup rotation — homepage only, one per pageview, after light
     scrolling, with a session cooldown so it never feels spammy. Pool = the owner's
     real offer programs. Respects the newsletter opt-out. */
  const setupDealRotation = () => {
    if (!document.querySelector('[data-stars-deal-rotation]')) return;
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path !== '/') return;
    const COOLDOWN_MS = 12000;
    try {
      const last = parseInt(window.sessionStorage.getItem('stars:deal-last') || '0', 10);
      if (Date.now() - last < COOLDOWN_MS) return;
    } catch (e) { /* continue */ }
    /* someone who just submitted a form shouldn't be hit with a promo popup */
    if (document.querySelector('.stars-form-success')) return;
    let pool = ['newsletter', 'firsttime', 'secondtime', 'bulkdeal', 'retail', 'referral'];
    try { if (window.localStorage.getItem('stars:nl-optout')) pool = pool.filter((n) => n !== 'newsletter'); } catch (e) { /* ignore */ }
    pool = pool.filter((n) => document.querySelector('[data-stars-modal="' + n + '"]'));
    /* America 250 window takes priority (once per session, never when expired) */
    try {
      if (document.querySelector('[data-stars-modal="a250"]')
          && !document.documentElement.classList.contains('stars-a250-expired')
          && !window.sessionStorage.getItem('stars:a250-pop')) pool = ['a250'];
    } catch (e) { /* ignore */ }
    if (!pool.length) return;
    /* deterministic-ish variety: rotate by a persisted counter, not Math.random
       (also keeps behavior reproducible for QA) */
    let idx = 0;
    try {
      idx = (parseInt(window.localStorage.getItem('stars:deal-idx') || '0', 10) + 1) % pool.length;
      window.localStorage.setItem('stars:deal-idx', String(idx));
    } catch (e) { /* ignore */ }
    const name = pool[idx];
    let fired = false;
    const onScroll = () => {
      if (fired) return;
      if (window.scrollY < 180) return;
      if (document.querySelector('.stars-modal[open], .stars-thanks')) return;
      /* the campaign window may have expired since page load — never open it stale */
      if (name === 'a250' && document.documentElement.classList.contains('stars-a250-expired')) {
        fired = true;
        window.removeEventListener('scroll', onScroll);
        return;
      }
      fired = true;
      window.removeEventListener('scroll', onScroll);
      const modal = document.querySelector('[data-stars-modal="' + name + '"]');
      if (!modal) return;
      modal.classList.remove('is-closing');
      modal.setAttribute('open', '');
      document.documentElement.classList.add('stars-modal-lock');
      try { window.sessionStorage.setItem('stars:deal-last', String(Date.now())); } catch (e) { /* ignore */ }
      if (name === 'a250') { try { window.sessionStorage.setItem('stars:a250-pop', '1'); } catch (e) { /* ignore */ } }
      const focusable = modal.querySelector('.stars-modal__x');
      if (focusable) focusable.focus();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  /* Live design previews — badge engine renders the badge photo with per-panel
     <textPath> lettering along stored path geometry, auto-shrinking the font
     until the text fits its path. */
  const setupLivePreviews = () => {
    /* ---- BADGE ---- */
    const bdPreview = document.querySelector('[data-bd-preview]');
    const geoEl = document.querySelector('[data-badge-geometry]');
    if (bdPreview && geoEl) {
      let GEO = {};
      try { GEO = JSON.parse(geoEl.textContent) || {}; } catch (e) { GEO = {}; }
      const FONTS = { 'Impact': "Impact, 'Arial Narrow Bold', sans-serif", 'Times New Roman': "'Times New Roman', Times, serif" };
      const COLORS = { 'Black': '#000000', 'Blue': '#0000FF', 'Red': '#FF0000', 'Green': '#005710' };
      const panelInputs = [...document.querySelectorAll('[data-bd-panel]')];
      const fontSel = document.querySelector('[data-bd-font]');
      const colorSel = document.querySelector('[data-bd-color]');
      const capsBox = document.querySelector('[data-bd-caps]');

      const currentSku = () => {
        const checked = document.querySelector('input[data-bd-sku]:checked');
        return checked ? checked.getAttribute('data-bd-sku') : null;
      };

      const syncPanels = (geoItem) => {
        const have = geoItem ? geoItem.p.map((pp) => String(pp[0])) : ['1', '2', '3', '4'];
        panelInputs.forEach((input) => {
          const id = input.getAttribute('data-bd-panel');
          const row = input.closest('label');
          const item = geoItem && geoItem.p.filter((pp) => String(pp[0]) === id)[0];
          if (row) row.style.display = have.indexOf(id) === -1 ? 'none' : '';
          if (item) {
            input.maxLength = item[2];
            input.placeholder = 'Up to ' + item[2] + ' characters';
            if (input.value.length > item[2]) input.value = input.value.slice(0, item[2]);
          }
        });
      };

      const render = () => {
        const sku = currentSku();
        const geoItem = sku && GEO[sku];
        if (!geoItem) { bdPreview.innerHTML = ''; return; }
        const font = FONTS[(fontSel && fontSel.value) || 'Impact'] || FONTS.Impact;
        const fill = COLORS[(colorSel && colorSel.value) || 'Black'] || '#000000';
        const caps = !capsBox || capsBox.checked;
        let svg = '<svg viewBox="0 0 ' + geoItem.w + ' ' + geoItem.h + '" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">';
        svg += '<defs>';
        geoItem.p.forEach((pp) => { svg += '<path id="bd-path-' + pp[0] + '" d="' + pp[3] + '" />'; });
        svg += '</defs>';
        svg += '<image x="0" y="0" width="100%" height="100%" href="' + geoItem.img + '" xlink:href="' + geoItem.img + '" />';
        geoItem.p.forEach((pp) => {
          const input = panelInputs.filter((el) => el.getAttribute('data-bd-panel') === String(pp[0]))[0];
          let value = input ? input.value : '';
          if (caps) value = value.toUpperCase();
          value = value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          svg += '<text font-family="' + font + '" font-weight="bold" font-size="' + pp[1] + '" fill="' + fill + '" text-anchor="middle">';
          svg += '<textPath id="bd-tp-' + pp[0] + '" href="#bd-path-' + pp[0] + '" xlink:href="#bd-path-' + pp[0] + '" startOffset="50%">' + value + '</textPath></text>';
        });
        svg += '</svg>';
        bdPreview.innerHTML = svg;
        /* auto-shrink: reduce font size until the text fits its path */
        geoItem.p.forEach((pp) => {
          const tp = bdPreview.querySelector('#bd-tp-' + pp[0]);
          const path = bdPreview.querySelector('#bd-path-' + pp[0]);
          if (!tp || !path || !tp.getComputedTextLength) return;
          let size = pp[1];
          let guard = 40;
          try {
            while (guard-- > 0 && tp.getComputedTextLength() * 1.05 > path.getTotalLength() && size > 6) {
              size -= 1;
              tp.parentNode.setAttribute('font-size', size);
            }
          } catch (e) { /* detached svg */ }
        });
      };

      document.querySelectorAll('input[data-bd-sku]').forEach((radio) =>
        radio.addEventListener('change', () => { syncPanels(GEO[radio.getAttribute('data-bd-sku')]); render(); })
      );
      panelInputs.forEach((input) => input.addEventListener('input', render));
      if (fontSel) fontSel.addEventListener('change', render);
      if (colorSel) colorSel.addEventListener('change', render);
      if (capsBox) capsBox.addEventListener('change', render);
      syncPanels(GEO[currentSku()]);
      render();
    }

    /* ---- NAMEPLATE ---- */
    const npPreview = document.querySelector('[data-np-preview]');
    if (npPreview) {
      const plate = npPreview.querySelector('[data-np-plate]');
      const line1 = npPreview.querySelector('[data-np-line1]');
      const line2 = npPreview.querySelector('[data-np-line2]');
      const renderNp = () => {
        const gold = document.querySelector('[data-np-color="gold"]');
        const isGold = !gold || gold.checked;
        if (plate) {
          plate.setAttribute('fill', isGold ? 'url(#npGold)' : 'url(#npSilver)');
          plate.setAttribute('stroke', isGold ? '#7a6420' : '#5d646e');
        }
        const styleRadio = document.querySelector('input[name="contact[Nameplate style]"]:checked');
        const blueInk = styleRadio && /blue ink/i.test(styleRadio.value);
        const ink = blueInk ? '#1636a8' : '#141414';
        const l1 = document.querySelector('[data-np-line="1"]');
        const l2 = document.querySelector('[data-np-line="2"]');
        if (line1) { line1.textContent = ((l1 && l1.value) || 'YOUR NAME').toUpperCase(); line1.setAttribute('fill', ink); }
        if (line2) { line2.textContent = ((l2 && l2.value) || '').toUpperCase(); line2.setAttribute('fill', ink); }
      };
      document.querySelectorAll('[data-np-line], [data-np-color], input[name="contact[Nameplate style]"]').forEach((el) => {
        el.addEventListener('input', renderNp);
        el.addEventListener('change', renderNp);
      });
      renderNp();
    }

    /* ---- PATCH ---- */
    const pdPreview = document.querySelector('[data-pd-preview]');
    if (pdPreview) {
      const img = pdPreview.querySelector('[data-pd-img]');
      const txt = pdPreview.querySelector('[data-pd-text]');
      const renderPd = () => {
        const checked = document.querySelector('input[name="contact[Patch template]"]:checked');
        if (img && checked) {
          const card = checked.closest('.stars-designer__style');
          const src = card ? card.querySelector('img') : null;
          if (src) { img.src = src.src; img.alt = checked.value; img.style.display = ''; }
          else { img.removeAttribute('src'); img.style.display = 'none'; }
        }
        const input = document.querySelector('[data-pd-textinput]');
        if (txt) txt.textContent = ((input && input.value) || '').toUpperCase();
      };
      document.querySelectorAll('input[name="contact[Patch template]"]').forEach((el) => el.addEventListener('change', renderPd));
      const ptext = document.querySelector('[data-pd-textinput]');
      if (ptext) ptext.addEventListener('input', renderPd);
      renderPd();
    }
  };

  /* Thank-you celebration — when any form round-trips with a success message
     (.stars-form-success), present it as an animated overlay: drawn checkmark,
     floating stars, and the form-specific confirmation text. */
  const setupThankYou = () => {
    /* remember which form is being submitted (contact posts reload the page,
       and Shopify marks EVERY contact form on the page as posted_successfully) */
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form && form.tagName === 'FORM' && form.id && (form.getAttribute('action') || '').indexOf('/contact') !== -1) {
        try { window.sessionStorage.setItem('stars:last-form', form.id); } catch (e) { /* ignore */ }
      }
    }, true);

    const all = [...document.querySelectorAll('.stars-form-success')];
    if (!all.length) return;
    /* resolve the submitted form: redirect hash first, then the stored id */
    const inForm = (id) => {
      if (!id) return null;
      const form = document.getElementById(id);
      return form && form.querySelector ? form.querySelector('.stars-form-success') : null;
    };
    let stored = null;
    try { stored = window.sessionStorage.getItem('stars:last-form'); window.sessionStorage.removeItem('stars:last-form'); } catch (e) { /* ignore */ }
    let successEl = inForm((window.location.hash || '').slice(1)) || inForm(stored);
    if (!successEl && all.length === 1) successEl = all[0];
    /* hide the phantom success lines on forms that were NOT submitted */
    all.forEach((el) => { if (el !== successEl) el.style.display = 'none'; });
    if (!successEl) return;
    let body = (successEl.textContent || '').trim().replace(/\s+/g, ' ');
    body = body.replace(/^thanks\s*(?:[—–-]\s*)?/i, '');
    if (body) body = body.charAt(0).toUpperCase() + body.slice(1);
    if (!body) body = 'Your request was received. We’ll get back to you shortly.';

    const overlay = document.createElement('div');
    overlay.className = 'stars-thanks';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'StarsThanksTitle');
    overlay.innerHTML =
      '<div class="stars-thanks__backdrop" data-thanks-close></div>' +
      '<div class="stars-thanks__card">' +
      '<span class="stars-thanks__star" aria-hidden="true">&#9733;</span>' +
      '<span class="stars-thanks__star" aria-hidden="true">&#9733;</span>' +
      '<span class="stars-thanks__star" aria-hidden="true">&#9733;</span>' +
      '<span class="stars-thanks__star" aria-hidden="true">&#9733;</span>' +
      '<span class="stars-thanks__star" aria-hidden="true">&#9733;</span>' +
      '<span class="stars-thanks__star" aria-hidden="true">&#9733;</span>' +
      '<svg class="stars-thanks__check" viewBox="0 0 96 96" aria-hidden="true">' +
      '<circle class="stars-thanks__check-ring" cx="48" cy="48" r="42" fill="none" stroke-width="5"/>' +
      '<path class="stars-thanks__check-mark" fill="none" stroke-width="7" stroke-linecap="round" stroke-linejoin="round" d="M30 50 L43 63 L67 36"/>' +
      '</svg>' +
      '<h2 id="StarsThanksTitle">Thank You!</h2>' +
      '<p></p>' +
      '<button type="button" class="stars-btn stars-btn--red" data-thanks-close>Continue</button>' +
      '</div>';
    overlay.querySelector('p').textContent = body;
    document.body.appendChild(overlay);
    document.documentElement.classList.add('stars-modal-lock');

    const dismiss = () => {
      overlay.classList.add('is-leaving');
      document.documentElement.classList.remove('stars-modal-lock');
      window.setTimeout(() => overlay.remove(), 340);
    };
    overlay.querySelectorAll('[data-thanks-close]').forEach((el) => el.addEventListener('click', dismiss));
    document.addEventListener('keydown', function onEsc(event) {
      if (event.key === 'Escape') { dismiss(); document.removeEventListener('keydown', onEsc); }
    });
    window.requestAnimationFrame(() => overlay.classList.add('is-showing'));
    const btn = overlay.querySelector('button');
    if (btn) btn.focus();
    /* strip contact_posted + hash so a refresh doesn't replay the celebration */
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('contact_posted');
      url.hash = '';
      window.history.replaceState(null, '', url.pathname + url.search);
    } catch (e) { /* ignore */ }
  };

  /* Input masks — phone fields accept digits only and auto-format as
     (XXX) XXX-XXXX; quantity fields are digits-only (typing and paste);
     emails get trimmed. Applies site-wide to every form. */
  const setupInputMasks = () => {
    document.querySelectorAll('input[type="tel"]').forEach((el) => {
      el.setAttribute('maxlength', '16');
      el.setAttribute('inputmode', 'tel');
      const format = () => {
        let digits = el.value.replace(/\D/g, '').slice(0, 11);
        if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
        let out = digits;
        if (digits.length > 6) out = '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6, 10);
        else if (digits.length > 3) out = '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
        el.value = out;
        if (el.value && digits.length < 10) el.setCustomValidity('Enter a 10-digit phone number');
        else el.setCustomValidity('');
      };
      el.addEventListener('input', format);
      el.addEventListener('blur', format);
      el.addEventListener('keypress', (event) => {
        if (event.key && event.key.length === 1 && !/[0-9]/.test(event.key)) event.preventDefault();
      });
      el.addEventListener('paste', (event) => {
        event.preventDefault();
        const text = ((event.clipboardData || window.clipboardData).getData('text') || '').replace(/\D/g, '');
        el.value = el.value + text;
        format();
      });
    });

    document.querySelectorAll('input[type="text"][inputmode="numeric"]').forEach((el) => {
      el.addEventListener('keypress', (event) => {
        if (event.key && event.key.length === 1 && !/[0-9]/.test(event.key)) event.preventDefault();
      });
      el.addEventListener('input', () => {
        const clean = el.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 6);
        if (clean !== el.value) el.value = clean;
      });
      el.addEventListener('paste', (event) => {
        event.preventDefault();
        const text = ((event.clipboardData || window.clipboardData).getData('text') || '').replace(/\D/g, '').slice(0, 6);
        el.value = text;
      });
    });

    document.querySelectorAll('input[type="email"]').forEach((el) => {
      el.addEventListener('blur', () => { el.value = el.value.trim(); });
    });
  };

  /* Prefill the custom-order "Product / item" field from ?product= (card quote helper). */
  const setupQuotePrefill = () => {
    const item = document.getElementById('co-item');
    if (!item) return;
    try {
      const q = new URLSearchParams(window.location.search).get('product');
      if (q && !item.value) item.value = q;
    } catch (e) { /* ignore */ }
  };

  /* Mobile bottom-nav search button focuses the header search field. */
  const setupBottomNav = () => {
    const btn = document.querySelector('[data-stars-bottom-search]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const input = document.getElementById('StarsHeaderSearch');
      if (input) {
        input.scrollIntoView({ block: 'center' });
        input.focus();
      } else {
        window.location.href = '/search';
      }
    });
  };

  ready(() => {
    setupReveal();
    setupMegaMenus();
    setupMobileMenu();
    setupOfferBar();
    setupShippingProgress();
    setupVariantSummary();
    setupPackageBuilder();
    setupRecentlyViewed();
    setupWishlist();
    setupModals();
    setupPrint();
    setupHelpWidget();
    setupBottomNav();
    setupProductCustomization();
    setupVariantGuard();
    setupStickyAtc();
    setupBackToTop();
    setupCondensingHeader();
    setupA250();
    setupToasts();
    setupCardSwatches();
    setupCartDrawer();
    setupQuotePrefill();
    setupDealRotation();
    setupDesigners();
    setupLivePreviews();
    setupInputMasks();
    setupThankYou();
  });
})();
